import React, { useState, useRef, useEffect } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { KanbanColumn } from './KanbanColumn';
import { ClientKanbanCard } from './ClientKanbanCard';
import { KanbanToolbar } from './controls/KanbanToolbar';
import { ZoomControls } from './controls/ZoomControls';
import { useKanbanLogic } from '@/hooks/useKanbanLogic';
import { Client } from '@/types/client';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';

export const ResponsiveKanbanBoard = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [activeClient, setActiveClient] = useState<Client | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [viewMode, setViewMode] = useState<'comfortable' | 'compact' | 'spacious'>('comfortable');
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  const boardRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  const {
    columns,
    groupedClients,
    filteredClients,
    updateClientStatus,
    resetLayout,
  } = useKanbanLogic(searchTerm, statusFilter);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  // Handle fullscreen
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await boardRef.current?.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (error) {
      console.error('Fullscreen error:', error);
      toast({
        title: "خطأ في ملء الشاشة",
        description: "تعذر تغيير وضع ملء الشاشة",
        variant: "destructive",
      });
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    const client = filteredClients.find(c => c.id === active.id);
    setActiveClient(client || null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveClient(null);

    if (!over) return;

    const clientId = active.id as string;
    const newStatus = over.id as Client['status'];

    // Find the client
    const client = filteredClients.find(c => c.id === clientId);
    if (!client || client.status === newStatus) return;

    // Update status using the mutation
    updateClientStatus(clientId, newStatus);
  };

  const handleSettings = () => {
    toast({
      title: "الإعدادات",
      description: "سيتم إضافة هذه الميزة قريباً",
    });
  };

  const getContainerClasses = () => {
    return cn(
      "h-full w-full overflow-hidden transition-all duration-300",
      "bg-gradient-to-br from-background via-muted/20 to-background",
      isFullscreen && "bg-background h-screen"
    );
  };

  const getBoardClasses = () => {
    return cn(
      "flex-1 overflow-hidden transition-all duration-300",
      !isFullscreen && "px-4 pb-4"
    );
  };

  const getZoomedContentClasses = () => {
    return cn(
      "h-full transition-transform duration-300 ease-out"
    );
  };

  const getColumnsContainerClasses = () => {
    return cn(
      "grid h-full w-full p-4 transition-all duration-300",
      "scrollbar-thin scrollbar-thumb-muted-foreground/20 scrollbar-track-transparent",
      zoomLevel !== 1 ? "overflow-visible" : "overflow-auto",
      viewMode === 'compact' && "gap-4 p-3",
      viewMode === 'comfortable' && "gap-6",
      viewMode === 'spacious' && "gap-8 p-6"
    );
  };

  return (
    <div ref={boardRef} className={getContainerClasses()}>
      {/* Toolbar */}
      {!isFullscreen && (
        <KanbanToolbar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          totalClients={filteredClients.length}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          onResetLayout={resetLayout}
          onSettings={handleSettings}
        />
      )}

      {/* Kanban Board */}
      <div className={getBoardClasses()}>
        <div 
          className={getZoomedContentClasses()}
          style={zoomLevel !== 1 ? {
            transform: `scale(${zoomLevel})`,
            transformOrigin: 'center',
            width: `${100 / zoomLevel}%`,
            height: `${100 / zoomLevel}%`,
          } : {}}
        >
          <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <SortableContext 
              items={columns.map(col => col.id)} 
              strategy={horizontalListSortingStrategy}
            >
              <div 
                className={getColumnsContainerClasses()}
                style={{
                  gridTemplateColumns: `repeat(${columns.length}, 1fr)`,
                }}
              >
                {columns.map((column) => (
                  <KanbanColumn
                    key={column.id}
                    column={column}
                    clients={groupedClients[column.status] || []}
                    viewMode={viewMode}
                  />
                ))}
              </div>
            </SortableContext>

            <DragOverlay>
              {activeClient ? (
                <ClientKanbanCard 
                  client={activeClient} 
                  isDragging 
                  viewMode={viewMode}
                />
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>
      </div>

      {/* Zoom Controls */}
      <ZoomControls
        zoomLevel={zoomLevel}
        onZoomChange={setZoomLevel}
        isFullscreen={isFullscreen}
        onToggleFullscreen={toggleFullscreen}
      />
    </div>
  );
};